import { z } from 'zod';
import { contactSchema } from './contact.schema';
import { configurationSetupSchema } from './configuration.schema';
import { callRecordSchema, callOutcomeSchema } from './call.schema';

export const campaignStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'FINISHED', 'STOPPED']);

export type CampaignStatus = z.infer<typeof campaignStatusSchema>;

// Channel a campaign runs over.
export const campaignChannelSchema = z.enum(['PSTN', 'WHATSAPP']);
export type CampaignChannel = z.infer<typeof campaignChannelSchema>;

// For WHATSAPP campaigns: how to fill the approved template per contact from
// keys on contact.data. body is the ordered {{1}}, {{2}}... variables.
export const waTemplateMappingSchema = z.object({
  header_image: z.string().optional(),
  body: z.array(z.string()).optional().default([]),
  button_url: z.string().optional(),
});
export type WaTemplateMapping = z.infer<typeof waTemplateMappingSchema>;

const waCampaignFields = {
  channel: campaignChannelSchema.optional(),
  outbound_whatsapp_phone_number_id: z.string().uuid().nullable().optional(),
  wa_template_name: z.string().nullable().optional(),
  wa_template_language: z.string().nullable().optional(),
  wa_template_mapping: waTemplateMappingSchema.nullable().optional(),
  // When true, a contact's reply triggers a WhatsApp voice call (AI) — see Phase 2.
  wa_call_on_reply: z.boolean().optional(),
  // When true, a contact the template never reached (send rejected, or Meta
  // reports delivery failed — usually no WhatsApp on that number) is called instead.
  wa_call_on_undelivered: z.boolean().optional(),
};

// Per-campaign retry configuration. `retryable_outcomes` are the CallOutcome
// values that trigger a redial; `outcome_source` picks whether the retry logic
// reads the raw or the reviewed (effective) outcome. Both are optional on write
// — the backend fills them from the CAMPAIGN_DEFAULT_* settings when omitted.
export const outcomeSourceSchema = z.enum(['RAW', 'REVIEWED']);
export type OutcomeSource = z.infer<typeof outcomeSourceSchema>;

const campaignRetryFields = {
  retryable_outcomes: z.array(callOutcomeSchema).optional(),
  outcome_source: outcomeSourceSchema.optional(),
};

export const reviewStatusSchema = z.enum(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED']);

export type ReviewStatus = z.infer<typeof reviewStatusSchema>;

export const campaignReviewSchema = z.object({
  id: z.string().uuid(),
  campaignId: z.string().uuid(),
  status: reviewStatusSchema,
  totalCalls: z.number(),
  reviewedCalls: z.number(),
  callsWithIssues: z.number(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type CampaignReview = z.infer<typeof campaignReviewSchema>;

export const campaignSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: campaignStatusSchema,
  configuration_id: z.string().uuid(),
  outbound_twilio_phone_number_id: z.string().uuid().nullable().optional(),
  outbound_telnyx_phone_number_id: z.string().uuid().nullable().optional(),
  workflow_id: z.string().uuid().nullable().optional(),

  runs_at: z.date().or(z.string().date()),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});

export type Campaign = z.infer<typeof campaignSchema>;

export const createCampaignSchema = z.object({
  name: z.string().min(2, 'Campaign name must be at least 2 characters'),
  configuration_id: z.string().uuid('Invalid configuration ID'),
  outbound_twilio_phone_number_id: z.string().uuid().optional().nullable(),
  outbound_telnyx_phone_number_id: z.string().uuid().optional().nullable(),
  workflow_id: z.string().uuid().optional().nullable(),
  ...waCampaignFields,
  ...campaignRetryFields,

  contacts: z
    .array(
      contactSchema.omit({ id: true }).extend({
        data: z.record(z.string(), z.unknown()).optional().nullable(),
      }),
    )
    .min(1, 'At least one contact is required'),
  runs_at: z.date().or(z.string().date()),
  // When a unique_identifier already belongs to another contact, update that
  // contact to this row's name + phone instead of failing the upload.
  force_contact_updates: z.boolean().optional(),
});

export type CreateCampaignDto = z.infer<typeof createCampaignSchema>;

/** A contact as shown in a campaign upload error. */
export interface CampaignContactRef {
  id: string;
  name: string | null;
  phone: string | null;
}

/**
 * One uploaded contact that could not be saved. Returned by campaign create
 * as `{ message, contact_errors: CampaignContactError[] }`.
 */
export interface CampaignContactError {
  /** 1-based position of the contact in the uploaded list. */
  position: number;
  name: string;
  phone: string | null;
  unique_identifier?: string;
  /** UNIQUE_IDENTIFIER_TAKEN can be fixed with force_contact_updates; DATABASE_ERROR cannot. */
  reason: 'UNIQUE_IDENTIFIER_TAKEN' | 'DATABASE_ERROR';
  /** Plain-English explanation, ready to show in the UI. */
  message: string;
  /** Existing contact this row's phone matched, when it matched one. */
  matched_contact?: CampaignContactRef;
  /** Contact that already owns the unique identifier. */
  identifier_owner?: CampaignContactRef;
}

export const updateCampaignSchema = z.object({
  name: z.string().min(2).optional(),
  status: campaignStatusSchema.optional(),
  configuration_id: z.string().uuid().optional(),
  outbound_twilio_phone_number_id: z.string().uuid().nullable().optional(),
  outbound_telnyx_phone_number_id: z.string().uuid().nullable().optional(),
  workflow_id: z.string().uuid().nullable().optional(),
  runs_at: z.date().or(z.string().date()).optional(),
  ...waCampaignFields,
});

export type UpdateCampaignDto = z.infer<typeof updateCampaignSchema>;

export const campaignDetailSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: campaignStatusSchema,
  configuration_id: z.string().uuid(),
  outbound_twilio_phone_number_id: z.string().uuid().nullable().optional(),
  outbound_telnyx_phone_number_id: z.string().uuid().nullable().optional(),
  configurationName: z.string(),
  configuration: configurationSetupSchema.optional(),
  contacts: z.array(contactSchema),
  calls: z.array(callRecordSchema.extend({ contact: contactSchema.optional() })).optional(),
  campaignReviews: z.array(campaignReviewSchema).optional(),
  callsCount: z.number().optional(),
  completedCallsCount: z.number().optional(),
  runsAt: z.string().date(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
  totalAvailabilityHours: z.number().optional(), // Total hours from availability captures (each shift = 12 hours)
  // Which data produced totalAvailabilityHours. Only set when
  // AVAILABILITY_SOURCE_TOOLS is on, i.e. when both sources are possible.
  availabilityHoursSource: z.enum(['tool', 'review']).optional(),
  retryable_outcomes: z.array(callOutcomeSchema).optional(),
  outcome_source: outcomeSourceSchema.optional(),
  // Channel the campaign runs over. The UI needs this to tell a WhatsApp campaign
  // apart from a voice one — a WhatsApp campaign has no outbound caller ID to pick.
  channel: campaignChannelSchema.optional(),
  wa_call_on_undelivered: z.boolean().optional(),
});

export type CampaignDetail = z.infer<typeof campaignDetailSchema>;

// Filter for campaigns with/without reviews
export const reviewFilterSchema = z.enum(['all', 'reviewed', 'not_reviewed']);
export type ReviewFilter = z.infer<typeof reviewFilterSchema>;

// Campaign query schema for pagination and filtering
export const campaignQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),
  search: z.string().optional(),
  status: campaignStatusSchema.optional(),
  configuration_id: z.string().uuid().optional(),
  configurationName: z.string().optional(),
  runsAt: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  sortBy: z.enum(['created_at', 'updated_at', 'name', 'status', 'runs_at']).default('created_at'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  has_reviews: reviewFilterSchema.optional(),
  has_calls: z.coerce.boolean().optional(),
});

export type CampaignQuery = z.infer<typeof campaignQuerySchema>;
